import {classificationTypes, disseminationOptionTypes, otherDissemOptionTypes, relToOptionTypes, sciOptionTypes, fgiOptionTypes, lacOptionTypes, topicOptionValueTypes, regionOptionValueTypes,
    disseminationOptionsOrder, otherDissemOptionsOrder, relToOptionsOrder, sciOptionsOrder, fgiOptionsOrder, lacOptionsOrder, topicOptionsValuesOrder, regionOptionsValuesOrder} from './constants/definedTypes';
import {Utilities} from './utils/Utilities';

export class ClassificationXMLObject {
    constructor(protected xmlns_class?: string, protected xmlns?: string, protected dateClassified?: string, protected portionMarking?: string, protected caveat?: boolean,
        protected classificationMarking?: ClassificationMarking, protected classificationHeader?: ClassificationHeader, protected classificationBlock?: ClassificationBlock,
        protected controlMarkings?: Array<ControlMarkings>, protected classificationFooter?: ClassificationFooter, protected intel?: Intel,
        protected isDisseminable?: IsDisseminable, protected needToKnow?: NeedToKnow) {}

    // Reference to Class Name after code has been minimized
    getClassName(): string {
        return 'ClassificationXMLObject';
    }

    getXmlnsClass(): string | undefined {
        return this.xmlns_class;
    }

    getXmlns(): string | undefined {
        return this.xmlns;
    }

    getClassificationDate(): string | undefined {
        return this.dateClassified;
    }

    getPortionMarking(): string | undefined {
        return this.portionMarking;
    }

    getCaveat(): boolean | undefined {
        return this.caveat;
    }

    getClassificationMarking(): ClassificationMarking | undefined {
        return this.classificationMarking;
    }

    getClassificationHeader(): ClassificationHeader | undefined {
        return this.classificationHeader;
    }

    getClassificationBlock(): ClassificationBlock | undefined {
        return this.classificationBlock;
    }

    getControlMarkings(): Array<ControlMarkings> | undefined {
        return this.controlMarkings;
    }

    getClassificationFooter(): ClassificationFooter | undefined {
        return this.classificationFooter;
    }

    getIntel(): Intel | undefined {
        return this.intel;
    }

    getDisseminable(): IsDisseminable | undefined {
        return this.isDisseminable;
    }

    getNeedToKnow(): NeedToKnow | undefined {
        return this.needToKnow;
    }

    getBanner(): string | undefined {
        // Remove Unknown Options
        const removeUnknown = true;
        let tempBanner;

        // Classification
        const classification = Utilities.getSafe(() => this.getClassificationMarking()!.getValue(), undefined);
        if (classification !== undefined) {
            tempBanner = classification;
        }

        // SCI Options
        // Get Array of SCI Items ['SI', 'TK']
        const sciItems = (Utilities.getSafe(() => this.getSCIOptions(removeUnknown), undefined));
        if (sciItems !== undefined && sciItems!.length !== 0) {
        // find Compartments within the SCI Controls for properer ordering according to
        // DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 6 d Use a hyphen without interjected spaces
        // to separate an SCI control system name and its compartment(s) when applicable,
        // e.g. "SI-GAMMA" or to separate multiple compartments from each other, e.g.
        // "SI-G-XXX". List multiple compartments alpha-numerically.
            const sciCompartmentObj = {};
            const sciArrayFinal: string[] = [];

            for (let i=0; i<sciItems.length; i++) {
                const sci = sciItems[i];

                const sciSplit = sci.split('-');
                // determine if there is a compartment
                if (sciSplit.length > 1) {
                    // If 1st compartment for a sci control create an array
                    if (!(sciSplit[0] in sciCompartmentObj)) {
                        sciCompartmentObj[sciSplit[0]] = [];
                    }
                    // Add the compartment to the list for the control
                    sciCompartmentObj[sciSplit[0]].push(sciSplit[1]);
                } else {
                    // If no compartment add to the final array
                    sciArrayFinal.push(sci);
                }
            }

            // Build the - delimited strings for SCI Controls
            for (const key in sciCompartmentObj) {
                // If there was a version w/o compartements added to the final array remove it
                const sciIdx = sciArrayFinal.indexOf(key);
                if (sciIdx >=0) {
                    sciArrayFinal.splice(sciIdx, 1);
                }
                // Sort the compartments alpha-numerically and add to the final array
                sciArrayFinal.push(key + '-' + sciCompartmentObj[key].sort().join('-'));
            }

            if (sciArrayFinal.length > 0) {
                tempBanner += '//' + sciArrayFinal.sort().join('/');
            }
        }

        // FGI Options
        // Get Array of FGI Items ['AUS' , 'CAN' , 'GBR' , 'NZL']
        const fgiItems = (Utilities.getSafe(() => this.getFGIOptions(removeUnknown), undefined));
        if (fgiItems !== undefined && fgiItems!.length !== 0) {
            // MDEV-1555: Accounting for the possibility of no fgis selected (closed FGI)
            if (fgiItems[0].indexOf('CONCEALED') >= 0) {
                tempBanner += '//FGI';
            } else {
                const fgis = fgiItems!.join(' ');
                tempBanner += '//FGI ' + fgis;
            }
        }

        // Dissemination Control order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 1b, (4)(a) Multiple entries may be chosen from the dissemination
        // control categories if applicable. If multiple entries are used, they are listed in the order which
        // they appear in Fig 25. items in () aren't in the Figure
        //
        // 8. Dissemination Controls
        // FOR OFFICICIAL USE ONLY (FOUO)
        // ORIGINATOR CONTROLLED (ORCON,ORCON-USGOV)
        // CONTROLLED IMAGERY (IMCON)
        // NOT RELEASABLE TO FOREIGN NATIONALS (NOFORN)
        // CAUTION-PROPRIETARY INFORMATION INVOLVED (PROPIN)
        // AUTHORIZED FOR RELEASE TO (RELTO)
        // RELEASABLE BY INFORMATION DISCLOSURE OFFICIAL (RELIDO)
        // FOREIGN INTELLIIGENCE SURVEILLANCE ACT (FISA)
        // DISPLAY ONLY
        //
        // The Intelligence Community Markings System Register and Manual
        // Effective: 30 August 2019 it specifies RISK SENSITIVE (RSEN) before FOUO
        // in the Dissemination Controls Section. Section 9 Other Dissemination
        // Controls specifies that the following controls should be separated from the
        // Dissemination Controls section by // and be in the following order:
        //
        // 9. Other Dissemination Controls
        // Limdis, Exclusive Distribution (EXDIS), No Distribution (NODIS), Sensitive
        // But Unclassified (SBU), Law Enforcement Sensitive (LES), and SSI
        // Get Array of dissemination Items ['FOUO', 'REL TO', 'LIMDIS']
        const disseminationItems = Utilities.getSafe(() => this.getDisseminationOptions(removeUnknown), undefined);
        if (disseminationItems !== undefined && disseminationItems!.length !== 0) {
            const dissemValues = [] as disseminationOptionTypes;
            const otherDissemValues = [] as otherDissemOptionTypes;
            disseminationItems.forEach((element) => {
                if (otherDissemOptionsOrder.indexOf(element) >= 0) {
                    otherDissemValues.push(element);
                } else {
                    dissemValues.push(element);
                }
            });
            if (dissemValues.length > 0) {
                // Build out banner string
                // REL TO shows list of countires separated by a ', '
                tempBanner += '//' + dissemValues!.map((obj) => {
                    if (obj === 'REL TO') {
                        return obj + ' ' + this.getRelToOptions()!.map((country) => country).join(', ');
                    } else {
                        return obj;
                    }
                }).join('/');
            }
            if (otherDissemValues.length > 0) {
                // Build out banner string
                tempBanner += '//' + otherDissemValues.join('/');
            }
        }

        // ACCM Options
        const accmValue = Utilities.getSafe(() => this.getACCMValue(), undefined);
        if (accmValue !== undefined) {
            if (Array.isArray(accmValue)) {
                tempBanner += '//ACCM-' + accmValue.join('/');
            } else {
                tempBanner += '//ACCM-' + accmValue;
            }
        }

        // Topics
        const topics = (Utilities.getSafe(() => this.getTopicOptions(), undefined));
        if (topics !== undefined) {
            tempBanner += ' ; Topics: ' + topics.join(', ');
        }

        // Regions
        const regions = (Utilities.getSafe(() => this.getRegionOptions(), undefined));
        if (regions !== undefined) {
            tempBanner += ' ; Regions: ' + regions.join(', ');
        }

        // SIGINT Options
        const intelItems = Utilities.getSafe(() => this.getIntel(), undefined);
        if (intelItems !== undefined) {
            if (this.getIsEvaluatedAndMinimized()) {
                tempBanner += ' ; SIGINT:Is Evaluated And Minimized';
                if (this.getIsDisseminable()) {
                    tempBanner += ' ; Is Disseminable';
                }
            } else {
                tempBanner += ' ; SIGINT:Raw';
            }
        }

        // LACs
        if (Utilities.getSafe(() => this.getLACOptions(), undefined) !== undefined) {
            let lacs = '';
            if (this.getLACOptions()!.length !== 0) {
                lacs = this.getLACOptions().join(' ; ');
                tempBanner += ' ; ; '+lacs;
            }
        }

        return tempBanner;
    }

    // Helper function to get Control Marknigs by variable "type": 'SCIControls, FGIMarkings, DisseminationControls
    // Returns first element of Array of type ControlMarkings[]
    // Filter function is expected to return only a single element, grabbing the first[0] element of ControlMarkings[] array
    getControlMarkingByType(type: string): ControlMarkings {
        return this.getControlMarkings()!.filter((controlMarking) => controlMarking.getType() === type)[0];
    }

    // Helper function to get Dissemination Control Markings variables: ['FOUO', 'REL TO', 'LIMDIS']
    // Returns Array of type ControlMarking[]:  ControlMarking { value: 'REL TO', type: undefined, groupSource: 'DNI', controlMarking: [] },
    getDisseminationControlMarkings(): ControlMarking[] | undefined {
        return this.getControlMarkingByType('DisseminationControls').getControlMarkings();
    }

    // Helper function to get Dissemination Control items: 'FOUO', 'REL TO', 'LIMDIS'
    // Returns Array: ['FOUO', 'REL TO', 'LIMDIS']
    // Sort Array based on order from definedTypes.ts -> disseminationOptionsOrder
    getDisseminationOptions(removeUnknown?: boolean): string[] | undefined {
        const disseminationItems = this.getDisseminationControlMarkings()!.map((dissemination) => dissemination.getValue());
        return Utilities.sortByDefinedOrder(disseminationItems, disseminationOptionsOrder, removeUnknown);
    }

    // Helper function to get Dissemination Control Markings by variable "type": 'FOUO', 'REL TO', 'LIMDIS', etc
    // Returns Array of type ControlMarking[]
    // Filter function is expected to return only a single element, grabbing the first[0] element of ControlMarkings[] array
    // and the subsequent ControlMarking[]  [ControlMarking { value: 'USA', type: 'CAPCOTrigraphs', groupSource: undefined, controlMarking: undefined }]
    getDisseminationControlMarkingsByType(type: string): ControlMarking[] | undefined {
        return this.getControlMarkingByType('DisseminationControls').getControlMarkings()!.filter((obj) => obj.getValue() === type)[0].getControlMarkingItems();
    }

    // Helper function to get REL TO items: 'USA', 'AUS'
    // Returns Array: ['USA', 'AUS']
    // Sort Array based on order from definedTypes.ts -> relToOptionsOrder
    getRelToOptions(removeUnknown?: boolean): string[] {
        const relToItems = this.getDisseminationControlMarkingsByType('REL TO')!.map((country) => country.getValue());
        const relToOptions = Utilities.sortByDefinedOrder(relToItems, relToOptionsOrder, removeUnknown);

        // REL TO order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 10 d (4) The format for REL TO banner and portion markings
        // is [classification]//REL TO [country codes]. Trigraphic country code shall be listed first
        // (USA first, followed by other countries in alphabetical order), followed by coalition or
        // international organization tetragraph codes in alphabetical order. Each code shall be
        // separated from the next by a comma and space.
        if (relToOptions !== undefined && relToOptions.length > 0) {
            const relToArray = [...relToOptions];

            // remove the default of USA b/c it goes first in the trigraphic order
            const usaCtryStr = 'USA';
            const usaCtryStrIdx = relToArray.indexOf(usaCtryStr);
            if (usaCtryStrIdx >=0) {
                relToArray.splice(usaCtryStrIdx, 1);
            }
            let relToCtry = [] as relToOptionTypes;
            const relToTetra = [] as relToOptionTypes;
            for (let j=0; j<relToArray.length; j++) {
                if (relToArray[j].length == 3) {
                    relToCtry.push(relToArray[j]);
                } else {
                    relToTetra.push(relToArray[j]);
                }
            }

            // sort the 2 categories of graphs, see above
            relToCtry.sort();
            relToTetra.sort();

            relToCtry = relToCtry.concat(relToTetra);
            if (usaCtryStrIdx >=0) {
                relToCtry.unshift(usaCtryStr);
            }

            return relToCtry;
        }
        return relToOptions;
    }

    // Helper function to get FGI Control Markings by variable "type": 'FGIMarkings'
    // Returns Array of type ControlMarking[]
    // Filter function is expected to return only a single element, grabbing the first[0] element of ControlMarkings[] array
    // and the subsequent ControlMarking[] items: ControlMarking { value: 'AUS', type: undefined, groupSource: undefined, controlMarking: undefined }
    getFGIControlMarkings(): ControlMarking[] | undefined {
        const fgiControlMarkings = this.getControlMarkingByType('FGIMarkings').getControlMarkings()!.filter((obj) => obj.getValue() === 'FGI');
        if (fgiControlMarkings.length > 0) {
            return fgiControlMarkings[0].getControlMarkingItems();
        }
        return [new ControlMarking('CONCEALED')];
    }

    // Helper function to get FGI items: 'USA', 'AUS'
    // Returns Array: ['USA', 'AUS']
    // Sort Array based on order from definedTypes.ts -> fgiOptionsOrder
    getFGIOptions(removeUnknown?: boolean): (string | undefined)[] {
        const fgiItems = this.getFGIControlMarkings()!.map((fgi) => fgi.getValue());
        const fgiOptions = Utilities.sortByDefinedOrder(fgiItems, fgiOptionsOrder, removeUnknown);

        // FGI order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 9  Exept as provided in paragraph 9.e.
        // of this section, use "FGI" with the trigraphic country code and
        // international organization tetragraphs in the banner line; portion
        // markings for included FGI shall be specified in section 4 of this enclosure.
        // If multiple governments and/or international organizatinos furnished informtion,
        // the country trigraphs shall be listed in alphabetical order followed by the
        // international tetragraphs in alphabetical order, each separated by a single
        // space.

        if (fgiOptions !== undefined && fgiOptions.length > 0) {
            const fgiArray = [...fgiOptions];

            let fgiCtry = [] as fgiOptionTypes;
            const fgiTetra = [] as fgiOptionTypes;
            for (let j=0; j<fgiArray.length; j++) {
                if (fgiArray[j].length == 3) {
                    fgiCtry.push(fgiArray[j]);
                } else {
                    fgiTetra.push(fgiArray[j]);
                }
            }

            // sort the 2 categories of graphs, see above
            fgiCtry.sort();
            fgiTetra.sort();

            fgiCtry = fgiCtry.concat(fgiTetra);

            return fgiCtry;
        }
        return fgiOptions;
    }

    // Helper function to get SCI Control Markings by variable "type": 'SCIControls'
    // Returns Array of type ControlMarking[]
    // and the subsequent ControlMarking[] items: ControlMarking { value: 'SI', type: undefined, groupSource: 'NSA', controlMarking: [] }
    getSCIControlMarkings(): ControlMarking[] | undefined {
        return this.getControlMarkingByType('SCIControls').getControlMarkings();
    }

    // Helper function to get SCI items: 'SI', 'TK'
    // Returns Array: ['SI', 'TK']
    // Sort Array based on order from definedTypes.ts -> sciOptionsOrder
    getSCIOptions(removeUnknown?: boolean): (string | undefined)[] {
        const sciItems = this.getSCIControlMarkings()!.map((sci) => sci.getValue());
        const sciOptionItems = Utilities.sortByDefinedOrder(sciItems, sciOptionsOrder, removeUnknown);
        // SCI Control order is specific specified in DoDM 5200.01-v2, February 24, 2012
        // Change 3, 05/14/2019 Enclosure 4, 6c Multiple SCI control system entries may be used if applicable.
        // When multiple entries, (whether published or unpublished) are used, list them alphabetically. Use
        // a single forward slash (/) as the separator between multiple SCI control system entries (see Figure 33).
        if (sciOptionItems !== undefined) {
            sciOptionItems.sort();
        }

        return sciOptionItems;
    }

    // Helper function to get ACCM Control Markings by variable "type": 'NonICMarkings'
    // Returns First Element in Array of type ControlMarking[]
    // Expected to return only a single element, grabbing the first[0] element of ControlMarkings[] array
    // ControlMarking[] items: ControlMarking { value: 'ACCM-', type: undefined, groupSource: 'OtherNonPublished', controlMarking: undefined }
    getACCMControlMarkings(): ControlMarking {
        return this.getControlMarkingByType('NonICMarkings').getControlMarkings()![0];
    }

    // Helper function to get ACCM Value
    // Returns String: ACCM Value
    getACCMValue(): string | undefined {
        return this.getACCMControlMarkings().getValue();
    }

    // Helper Function to get Classification
    getClassification(): string | undefined {
        return this.getClassificationMarking()!.getValue();
    }

    // Helper Function to get Declassification Date
    getDeClassificationDate(): string | undefined {
        return this.getClassificationBlock()!.getText()!;
    }

    // Helper Function to Determine if SIGINT is Raw
    getIsRawSIGINT(): boolean | undefined {
        let isRaw = false;
        const intelItems = Utilities.getSafe(() => this.getIntel(), undefined);
        if (intelItems !== undefined) {
            if (!(this.getIsEvaluatedAndMinimized()) && !(Utilities.getSafe(() => this.getIsDisseminable(), undefined) !== undefined && this.getIsDisseminable())) {
                isRaw = true;
            }
        }
        return isRaw;
    }

    // Helper Function to Determine if SIGINT IsEvaluatedAndMinimized
    getIsEvaluatedAndMinimized(): boolean | undefined {
        let evaluatedAndMinimized;
        if (this.getIntel() !== undefined && this.getIntel()!.getIsEvaluatedAndMinimized() !== undefined && this.getIntel()!.getIsEvaluatedAndMinimized()!.getValue() === 'true') {
            evaluatedAndMinimized = true;
        }
        return evaluatedAndMinimized;
    }

    // Helper Function to Determine if SIGINT IsDisseminable
    getIsDisseminable(): boolean | undefined {
        let isDisseminable;
        if (this.getDisseminable() !== undefined && this.getDisseminable()!.getValue() === 'true') {
            isDisseminable = true;
        }
        return isDisseminable;
    }

    // Helper function to get LAC items: 'EO12333_D5240_NONSIGINT_NES', 'DISSEM_CYBER'
    // Returns Array: ['EO12333_D5240_NONSIGINT_NES', 'DISSEM_CYBER']
    // Sort Array based on order from definedTypes.ts -> lacOptionsOrder
    getLACOptions(): (string | undefined)[] {
        // Do not remove unknown values to accept user inputted LACs
        const removeUnknown = false;
        const lacItems = this.getNeedToKnow()!.getLACs()!.map((lac) => lac.getText());
        return Utilities.sortByDefinedOrder(lacItems, lacOptionsOrder, removeUnknown);
    }

    // Helper Function to Get Need To Know Topics
    getTopicOptions(removeUnknown?: boolean): Array<Topic> | undefined {
        const topicItems = this.getNeedToKnow()!.getNIPF()!.getTopics()!.map((topic) => topic.getText());
        return Utilities.sortByDefinedOrder(topicItems, topicOptionsValuesOrder, removeUnknown);
    }

    // Helper Function to Get Need To Know Regions
    getRegionOptions(removeUnknown?: boolean): Array<Region> | undefined {
        const regionItems = this.getNeedToKnow()!.getNIPF()!.getRegions()!.map((region) => region.getText());
        return Utilities.sortByDefinedOrder(regionItems, regionOptionsValuesOrder, removeUnknown);
    }

    // Helper Function to Get Need To Know COIs
    getCOIOptions(): (string | undefined)[] {
        return this.getNeedToKnow()!.getCOIs()!.map((coi) => coi.getText());
    }

    // Helper Function to Get Need To Know BRFs
    getBRFs(): (string | undefined)[] {
        return this.getNeedToKnow()!.getBRFs()!.map((brf) => brf.getText());
    }

    // Helper Function to Get Need To Know Organization
    getOrganization(): string | undefined {
        return this.getNeedToKnow()!.getOrganization()!.getText();
    }

    // Return JSON string of Object
    serialize(): string {
        // <TODO>
        // Update function so JSON is returned matching the xml to JSON output
        // This will allow json to be converted back to xml in the same format
        return JSON.stringify(this);
    }

    // Map JSON string to classificationXMLObject
    static parse(jsonString: string): ClassificationXMLObject {
        const data = JSON.parse(jsonString);

        // class:Classification
        const classificationAttributes = data['class:Classification']['_attributes'];
        const xmlns_class = classificationAttributes['xmlns:class'];
        const xmlns = classificationAttributes['xmlns'];
        const dateClassified = classificationAttributes['dateClassified'];
        const portionMarking = classificationAttributes['portionMarking'];
        const caveat = classificationAttributes['caveat'];

        // class:ClassificationMarking
        const classificationMarkingAttributes = data['class:Classification']['class:ClassificationMarking']['_attributes'];
        const classificationMarkingObject = new ClassificationMarking(classificationMarkingAttributes['type'], classificationMarkingAttributes['value']);

        // class:ClassificationHeader
        // class:ClassificationBanner
        const classificationHeaderBanner = new ClassificationBanner(data['class:Classification']['class:ClassificationHeader']['class:ClassificationBanner']);
        const classificationHeaderObject = new ClassificationHeader(classificationHeaderBanner);

        // class:ClassificationBlock
        // class:DeclOn
        const declOnObject = new DeclOn(data['class:Classification']['class:ClassificationBlock']['class:DeclOn']['_attributes']['type'],
            data['class:Classification']['class:ClassificationBlock']['class:DeclOn']['_attributes']['restrictedData']);
        const classificationBlockObject = new ClassificationBlock(declOnObject, data['class:Classification']['class:ClassificationBlock']['class:DeclOn']['_text']);

        // class:ControlMarkings
        // class:ControlMarking
        // SCIControls, FGIMarkings, REL TO DisseminationControls extraction
        let controlMarkingsData = data['class:Classification']['class:ControlMarkings'];
        let controlMarkings: Array<ControlMarkings> | undefined;
        // ControlMarkings are not mandatory, only extract if data is there
        if ( controlMarkingsData !== undefined ) {
            // ControlMarkings contain markings for SCI, FGI, and REL TO Options
            // Similar in structure, attributes are do not match 100% - must account for all the different variables
            // "class:ControlMarkings": [
            //    {"_attributes": { "type": "SCIControls" },
            //        "class:ControlMarking": { "_attributes": { "value": "SI", "groupSource": "NSA" } } },
            //    {"_attributes": { "type": "FGIMarkings" },
            //        "class:ControlMarking": { "_attributes": { "value": "FGI", "groupSource": "Respective Country" },
            //            "class:ControlMarking": [
            //                { "_attributes": { "value": "AUS" } },
            //                { "_attributes": { "value": "GBR" } } ] } },
            //    {"_attributes": { "type": "DisseminationControls" },
            //        "class:ControlMarking": [ {"_attributes": { "value": "REL TO","groupSource": "DNI" },
            //            "class:ControlMarking": [
            //                  { "_attributes": { "value": "USA", "type": "CAPCOTrigraphs" } },
            //                  { "_attributes": { "value": "FVEY" } },
            //                  { "_attributes": { "value": "CAN" } } ] },
            //                  { "_attributes": { "type": "CAPCOTrigraphs", "value": "REL TO USA, FVEY, CAN" } } ] } ]

            // *Force data['class:Classification']['class:ControlMarkings'] to be of type Array*
            // If control Markings only contains one of the following (SCI, FGI, REL TO) it is sent as a single object instead of Array
            // Forcing it to Array Type allows easier code handling to extract values
            // "class:ControlMarkings": {
            //      "_attributes": { "type": "DisseminationControls" },
            //      "class:ControlMarking": [
            //              { "_attributes": { "value": "REL TO", "groupSource": "DNI" },
            //              "class:ControlMarking": { "_attributes": { "value": "USA", "type": "CAPCOTrigraphs"  } } },
            //              { "_attributes": { "type": "CAPCOTrigraphs", "value": "REL TO USA" } } ]
            //      }
            // Test if array if not turn into one
            if (!Array.isArray(controlMarkingsData)) {
                controlMarkingsData = [];
                controlMarkingsData.push(data['class:Classification']['class:ControlMarkings']);
            }
            controlMarkings = [];
            // Loop through each type of class:ControlMarking (SCIControls, FGIMarkings, DisseminationControls )
            controlMarkingsData.forEach((element) => {
                const lvl1_type = element['_attributes']['type'];
                const lvl2: Array<ControlMarking> = [];
                let subControlMarking = Utilities.getSafe(() => element['class:ControlMarking'], undefined);
                // *Force element['class:ControlMarking'] to be of type Array*
                // If ControlMarking only contains a single attribute it is sent as a single object instead of Array
                // Forcing it to Array Type allows easier code handling to extract values
                // "class:ControlMarking": { "_attributes": { "value": "FGI", "groupSource": "Respective Country" },
                if (!Array.isArray(subControlMarking)) {
                    subControlMarking = [];
                    subControlMarking.push(element['class:ControlMarking']);
                }

                if (subControlMarking !== undefined && Array.isArray(subControlMarking)) {
                    subControlMarking.forEach((element) => {
                        const lvl2B_value = Utilities.getSafe(() => element['_attributes']['value'], undefined);
                        const lvl2B_type = Utilities.getSafe(() => element['_attributes']['type'], undefined);
                        const lvl2B_groupSource = Utilities.getSafe(() => element['_attributes']['groupSource'], undefined);
                        const lvl3B: Array<ControlMarking> = [];
                        // FGI & REL TO have contain a 2nd level of class:ControlMarking below code extracts those values
                        // "class:ControlMarking": [  { "_attributes": { "value": "USA", "type": "CAPCOTrigraphs" } }
                        if (Utilities.getSafe(() => element['class:ControlMarking'], undefined) !== undefined) {
                            let subControlMarkingLvl3 = Utilities.getSafe(() => element['class:ControlMarking'], undefined);
                            if (!Array.isArray(subControlMarkingLvl3)) {
                                subControlMarkingLvl3 = [];
                                subControlMarkingLvl3.push(element['class:ControlMarking']);
                            }

                            subControlMarkingLvl3.forEach((element) => {
                                const lvl3B_value = Utilities.getSafe(() => element['_attributes']['value'], undefined);
                                const lvl3B_type = Utilities.getSafe(() => element['_attributes']['type'], undefined);
                                const lvl3B_groupSource = Utilities.getSafe(() => element['_attributes']['groupSource'], undefined);
                                lvl3B.push(new ControlMarking(lvl3B_value, lvl3B_type, lvl3B_groupSource, undefined));
                            });
                        }
                        if (lvl2B_value === 'FGI' && lvl3B.length === 0) {
                            lvl3B.push(new ControlMarking('CONCEALED'));
                        }
                        lvl2.push(new ControlMarking(lvl2B_value, lvl2B_type, lvl2B_groupSource, lvl3B));
                    });
                }

            controlMarkings!.push(new ControlMarkings(lvl1_type, lvl2));
            });
        }

        // class:ClassificationFooter
        // class:ClassificationBanner
        const classificationFooterBanner = new ClassificationBanner(data['class:Classification']['class:ClassificationFooter']['class:ClassificationBanner']);
        const classificationFooterObject = new ClassificationFooter(classificationFooterBanner);

        // class:Intel
        const intelData = Utilities.getSafe(() => data['class:Classification']['class:Intel'], undefined);

        // class:IsEvaluatedAndMinimized
        const isEvaluatedAndMinimizedObject = ( Utilities.getSafe(() => data['class:Classification']['class:Intel']['class:IsEvaluatedAndMinimized']['_attributes']['value'], undefined) !== undefined)?
            new IsEvaluatedAndMinimized(data['class:Classification']['class:Intel']['class:IsEvaluatedAndMinimized']['_attributes']['value']) : undefined;
        const intel = (intelData !== undefined) ? new Intel(intelData['_attributes']['type'], intelData['_attributes']['value'], isEvaluatedAndMinimizedObject) : undefined;

        // class:IsDisseminable
        const isDisseminable = ( Utilities.getSafe(() => data['class:Classification']['class:IsDisseminable']['_attributes']['value'], undefined) !== undefined)?
            new IsDisseminable(data['class:Classification']['class:IsDisseminable']['_attributes']['value']) : undefined;

        // NeedToKnow
        let needToKnow: NeedToKnow | undefined;
        const needToKnowData = Utilities.getSafe(() => data['class:Classification']['NeedToKnow'], undefined);
        if (needToKnowData !== undefined) {
            // LACs"
            let lacs: Array<LAC> | undefined;
            if ( Utilities.getSafe(() => needToKnowData['LACs']['LAC'], undefined) !== undefined ) {
                // *Force needToKnowData['LACs']['LAC'] to be of type Array*
                // If ['LAC'] only contains a single attribute it is sent as a single object instead of Array
                // Forcing it to Array Type allows easier code handling to extract values
                // "LACs": { "LAC": { "_text": "EO12333_D5240_NONSIGINT_NES" } }
                let lacsArrayData = needToKnowData['LACs']['LAC'];
                if (!Array.isArray(lacsArrayData)) {
                    lacsArrayData = [];
                    lacsArrayData.push(needToKnowData['LACs']['LAC']);
                }
                lacsArrayData.forEach((element) => {
                    (lacs! = lacs! || []).push(new LAC(element['_text']));
                });
            }

            // NIPF
            let nipf: NIPF | undefined;
            if ( Utilities.getSafe(() => needToKnowData['NIPF']['Topics']['Topic'], undefined) !== undefined || Utilities.getSafe(() => needToKnowData['NIPF']['Regions']['Region'], undefined) !== undefined) {
                // topics
                let topics: Array<Topic> | undefined;
                if ( Utilities.getSafe(() => needToKnowData['NIPF']['Topics']['Topic'], undefined) !== undefined ) {
                    // *Force needToKnowData['NIPF']['Topics']['Topic'] to be of type Array*
                    // If ['Topic'] only contains a single attribute it is sent as a single object instead of Array
                    // Forcing it to Array Type allows easier code handling to extract values
                    // "Topics": { "Topic": { "_text": "CN" } }
                    let toipcsArrayData = needToKnowData['NIPF']['Topics']['Topic'];
                    if (!Array.isArray(toipcsArrayData)) {
                        toipcsArrayData = [];
                        toipcsArrayData.push(needToKnowData['NIPF']['Topics']['Topic']);
                    }
                    toipcsArrayData.forEach((element) => {
                        (topics! = topics! || []).push(new Topic(element['_text']));
                    });
                }

                // regions
                let regions: Array<Region> | undefined;
                if ( Utilities.getSafe(() => needToKnowData['NIPF']['Topics']['Topic'], undefined) !== undefined ) {
                    // *Force needToKnowData['NIPF']['Topics']['Topic'] to be of type Array*
                    // If ['Region'] only contains a single attribute it is sent as a single object instead of Array
                    // Forcing it to Array Type allows easier code handling to extract values
                    // "Regions": { "Region": { "_text": "AFce" } }
                    let regionsArrayData = needToKnowData['NIPF']['Regions']['Region'];
                    if (!Array.isArray(regionsArrayData)) {
                        regionsArrayData = [];
                        regionsArrayData.push(needToKnowData['NIPF']['Regions']['Region']);
                    }
                    regionsArrayData.forEach((element) => {
                        (regions! = regions! || []).push(new Region(element['_text']));
                    });
                }
                nipf = new NIPF(topics, regions);
            }

            // COIs
            let cois: Array<COI> | undefined;
            if ( Utilities.getSafe(() => needToKnowData['COIs']['COI'], undefined) !== undefined ) {
                // *Force needToKnowData['COIs']['COI'] to be of type Array*
                // If ['COI'] only contains a single attribute it is sent as a single object instead of Array
                // Forcing it to Array Type allows easier code handling to extract values
                // "COIs": { "COI": { "_text": "coi1" } }
                let coisArrayData = needToKnowData['COIs']['COI'];
                if (!Array.isArray(coisArrayData)) {
                    coisArrayData = [];
                    coisArrayData.push(needToKnowData['COIs']['COI']);
                }
                coisArrayData.forEach((element) => {
                    (cois! = cois! || []).push(new COI(element['_text']));
                });
            }

            // BRFs
            let brfs: Array<BRF> | undefined;
            if ( Utilities.getSafe(() => needToKnowData['BRFs']['BRF'], undefined) !== undefined ) {
                // *Force needToKnowData['BRFs']['BRF'] to be of type Array*
                // If ['BRF'] only contains a single attribute it is sent as a single object instead of Array
                // Forcing it to Array Type allows easier code handling to extract values
                // "BRFs": { "BRF": { "_text": "I Agree" } }
                let brfsArrayData = needToKnowData['BRFs']['BRF'];
                if (!Array.isArray(brfsArrayData)) {
                    brfsArrayData = [];
                    brfsArrayData.push(needToKnowData['BRFs']['BRF']);
                }
                brfsArrayData.forEach((element) => {
                    (brfs! = brfs! || []).push(new BRF(element['_text']));
                });
            }
            // Organization
            let organization: Organization | undefined;
            if ( Utilities.getSafe(() => needToKnowData['Organization'], undefined) !== undefined ) {
                organization = new Organization(needToKnowData['Organization']['_text']);
            }

            needToKnow = new NeedToKnow(lacs, nipf, cois, brfs, organization);
        }

        return new ClassificationXMLObject(xmlns_class, xmlns, dateClassified, portionMarking, caveat, classificationMarkingObject, classificationHeaderObject,
            classificationBlockObject, controlMarkings, classificationFooterObject, intel, isDisseminable, needToKnow);
    }

    toClassificationJSONObject(): string {
        const removeUnknown = true;
        const jsonData = {};

        // classification "UNCLASSIFIED"
        if (Utilities.getSafe(() => this.getClassificationMarking()!.getValue(), undefined) !== undefined) {
            jsonData['classification'] = this.getClassificationMarking()!.getValue();
        }

        // banner: "UNCLASSIFIED//FOUO"
        if (Utilities.getSafe(() => this.getBanner(), undefined) !== undefined) {
            jsonData['banner'] = this.getBanner();
        }

        // disseminationOptions: ["NONE", "FOUO", "REL TO"]
        if (Utilities.getSafe(() => this.getControlMarkingByType('DisseminationControls'), undefined) !== undefined) {
            jsonData['disseminationOptions'] = this.getDisseminationOptions(removeUnknown);
        }

        // relToOptions: ["USA", "FVEY"]
        if (Utilities.getSafe(() => this.getDisseminationControlMarkingsByType('REL TO'), undefined) !== undefined) {
            jsonData['relToOptions'] = this.getDisseminationControlMarkingsByType('REL TO')!.map((rel) => rel.getValue());
        }

        // sciOptions: ["SI", "TK"]
        if (Utilities.getSafe(() => this.getControlMarkingByType('SCIControls'), undefined) !== undefined) {
            jsonData['sciOptions'] = this.getSCIControlMarkings()!.map((sci) => sci.getValue());
        }

        // fgiOptions: ["AUS", "GBR" ]
        if (Utilities.getSafe(() => this.getControlMarkingByType('FGIMarkings'), undefined) !== undefined) {
            jsonData['fgiOptions'] = this.getFGIControlMarkings()!.map((fgi) => fgi.getValue());
        }

        // "classificationDate": "2019-09-06"
        if (Utilities.getSafe(() => this.getClassificationDate(), undefined) !== undefined) {
            jsonData['classificationDate'] = this.getClassificationDate();
        }

        // declassificationDate: 20450101 or "Manual Review"
        if (Utilities.getSafe(() => this.getDeClassificationDate(), undefined) !== undefined) {
            jsonData['declassificationDate'] = this.getDeClassificationDate();
        }

        const intelItems = Utilities.getSafe(() => this.getIntel(), undefined);
        if (intelItems !== undefined) {
            // isEvaluatedAndMinimized: boolean
            if (this.getIsEvaluatedAndMinimized()) {
                jsonData['isEvaluatedAndMinimized'] = true;
                // isDisseminable: boolean
                if (this.getIsDisseminable()) {
                    jsonData['isDisseminable'] = true;
                }
            // isRawSigint: boolean (If Intel Object Exists but isEvalAndMin and isDisseminable are both false/unchecked)
            } else {
                jsonData['isRawSigint'] = true;
            }
        }

        // lacOptions: ["DISSEM_CYBER", "DISSEM_FI_SIGINT"]
        const lacOptions = (Utilities.getSafe(() => this.getLACOptions(), undefined));
        if (lacOptions !== undefined) {
            jsonData['lacOptions'] = lacOptions;
        }

        // accmValue: string, "topicOptions": [], "regionOptions"[]}
        let ACCMValue = (Utilities.getSafe(() => this.getACCMValue(), undefined));
        if (ACCMValue !== undefined ) {
            // Remove ACCM-
            if (ACCMValue.startsWith('ACCM-')) {
                ACCMValue = ACCMValue.slice(5);
            }
            jsonData['accmValue'] = ACCMValue;
        }

        // topicOptions: ["ANY", "CN"]
        const topicOptions = (Utilities.getSafe(() => this.getTopicOptions(), undefined));
        if (topicOptions !== undefined) {
            jsonData['topicOptions'] = topicOptions;
        }

        // regionOptions: ["ANAN", "AFce"]
        const regionOptions = (Utilities.getSafe(() => this.getRegionOptions(), undefined));
        if (regionOptions !== undefined) {
            jsonData['regionOptions'] = regionOptions;
        }

        // coiOptions: ["co1", "coi2"]
        const coiOptions = (Utilities.getSafe(() => this.getCOIOptions(), undefined));
        if (coiOptions !== undefined) {
            jsonData['coiOptions'] = coiOptions;
        }

        // brfs: ["I Agree", "brf1"]
        const brfs = (Utilities.getSafe(() => this.getBRFs(), undefined));
        if (brfs !== undefined) {
            jsonData['brfs'] = brfs;
        }

        // organization: string
        const organization = (Utilities.getSafe(() => this.getOrganization(), undefined));
        if (organization !== undefined) {
            jsonData['organization'] = organization;
        }

        // Return Final JSON Object
        return JSON.stringify(jsonData);
    }
}

export class ClassificationMarking {
    constructor(protected type?: string, protected value?: classificationTypes) {}

    getType(): string | undefined {
        return this.type;
    }

    getValue(): classificationTypes | undefined {
        return this.value;
    }
}

export class ClassificationHeader {
    constructor(protected classificationBannerObject?: ClassificationBanner) {}

    getBanner(): ClassificationBanner | undefined {
        return this.classificationBannerObject;
    }
}

export class ClassificationBanner {
    constructor(protected text?: string) {}

    getText(): string | undefined {
        return this.text;
    }
}

export class ClassificationBlock {
    constructor(protected declOnObject?: DeclOn, protected text?: string) {}

    getText(): string | undefined {
        return this.text;
    }

    getDeclOn(): DeclOn | undefined {
        return this.declOnObject;
    }
}

export class ControlMarkings {
    constructor(protected type?: string, protected controlMarkings?: Array<ControlMarking>) {}

    getType(): string | undefined {
        return this.type;
    }

    getControlMarkings(): Array<ControlMarking> | undefined {
        return this.controlMarkings;
    }
}

export class ControlMarking {
    constructor(protected value: string, protected type?: string, protected groupSource?: string, protected controlMarking?: Array<ControlMarking>) {}

    getValue(): string | undefined {
        return this.value;
    }

    getType(): string | undefined {
        return this.type;
    }

    getGroupSource(): string | undefined {
        return this.groupSource;
    }

    getControlMarkingItems(): Array<ControlMarking> | undefined {
        return this.controlMarking;
    }
}

export class DeclOn {
    constructor(protected type?: string, protected restrictedData?: boolean) {}

    getType(): string | undefined {
        return this.type;
    }

    getRestrictedData(): boolean | undefined {
        return this.restrictedData;
    }
}

export class ClassificationFooter {
    constructor(protected classificationBannerObject?: ClassificationBanner) {}

    getBanner(): ClassificationBanner | undefined {
        return this.classificationBannerObject;
    }
}

export class Intel {
    constructor(protected type?: string, protected value?: boolean, protected isEvaluatedAndMinimizedObject?: IsEvaluatedAndMinimized) {}

    getType(): string | undefined {
        return this.type;
    }

    getValue(): boolean | undefined {
        return this.value;
    }

    getIsEvaluatedAndMinimized(): IsEvaluatedAndMinimized | undefined {
        return this.isEvaluatedAndMinimizedObject;
    }
}

export class IsEvaluatedAndMinimized {
    constructor(protected value?: string) {}

    getValue(): string | undefined {
        return this.value;
    }
}

export class IsDisseminable {
    constructor(protected value?: string) {}

    getValue(): string | undefined {
        return this.value;
    }
}

export class NeedToKnow {
    constructor(protected LACs?: Array<LAC>, protected NIPF?: NIPF, protected COIs?: Array<COI>, protected BRFs?: Array<BRF>, protected organization?: Organization) {}

    getLACs(): Array<LAC> | undefined {
        return this.LACs;
    }

    getNIPF(): NIPF | undefined {
        return this.NIPF;
    }

    getCOIs(): Array<COI> | undefined {
        return this.COIs;
    }

    getBRFs(): Array<BRF> | undefined {
        return this.BRFs;
    }

    getOrganization(): Organization | undefined {
        return this.organization;
    }
}

export class LAC {
    constructor(protected text?: lacOptionTypes) {}

    getText(): lacOptionTypes | undefined {
        return this.text;
    }
}

export class NIPF {
    constructor(protected topics?: Array<Topic>, protected regions?: Array<Region>) {}

    getTopics(): Array<Topic> | undefined {
        return this.topics;
    }

    getRegions(): Array<Region> | undefined {
        return this.regions;
    }
}

export class Topic {
    constructor(protected text?: topicOptionValueTypes) {}

    getText(): topicOptionValueTypes | undefined {
        return this.text;
    }
}

export class Region {
    constructor(protected text?: regionOptionValueTypes) {}

    getText(): regionOptionValueTypes | undefined {
        return this.text;
    }
}

export class COI {
    constructor(protected text?: string) {}

    getText(): string | undefined {
        return this.text;
    }
}

export class BRF {
    constructor(protected text?: string) {}

    getText(): string | undefined {
        return this.text;
    }
}

export class Organization {
    constructor(protected text?: string) {}

    getText(): string | undefined {
        return this.text;
    }
}
